import { useCallback, useState } from 'react';
import { useConfigValuesState } from '../../hooks/useConfigValue';

/**
 * A custom React hook to manage time and calendar configuration settings.
 *
 * This hook provides functionality to:
 * - Manage and validate the default daily capacity value (0-24 hours)
 * - Handle user input changes and validation
 * - Update the configuration in the global state
 *
 * @returns {Object} Returns an object containing:
 *  - `defaultCapacity` (string) - The current default capacity value
 *  - `onChange` (function) - Handler for input changes
 *  - `onBlur` (function) - Validation handler when input loses focus
 *  - `error` (string) - Error message if validation fails
 *
 * @example
 * const TimeConfig = () => {
 *   const { defaultCapacity, onChange, onBlur, error } = useTimeAndCalendarGridConfig();
 *
 *   return (
 *     <TextField
 *       value={defaultCapacity}
 *       onChange={onChange}
 *       onBlur={onBlur}
 *       errorMessage={error}
 *     />
 *   );
 * };
 */
export function useTimeAndCalendarGridConfig() {
    const {
        initialData: {
            configuration: { capacityDefault },
        },
        setConfigValue,
    } = useConfigValuesState();
    const [defaultCapacity, setDefaultCapacity] = useState<string>(capacityDefault.toString());
    const [error, setError] = useState<string>('');

    /**
     * Handles changes to the capacity input field
     * Clears any existing error messages
     */
    const onChange = (_, newValue: string) => {
        setDefaultCapacity(newValue);
        setError('');
    };

    /**
     * Updates the global configuration with the new capacity value
     */
    const updateDefaultCapacity = useCallback(
        (newCapacity: number) => {
            setConfigValue(currentConfig => ({
                ...currentConfig,
                capacityDefault: newCapacity,
            }));
        },
        [setConfigValue],
    );

    /**
     * Validates the capacity value when the input loses focus
     * Ensures the value is between 0 and 24 hours
     * Reverts to previous value and shows error if validation fails
     */
    const onBlur = () => {
        const parsedNumber = Number(defaultCapacity);
        if (!isNaN(parsedNumber) && parsedNumber >= 0 && parsedNumber <= 24) {
            updateDefaultCapacity(parsedNumber);
            setDefaultCapacity(parsedNumber.toString());
        } else {
            setDefaultCapacity(capacityDefault.toString());
            setError('Please enter a valid number between 0 and 24');
        }
    };

    return {
        defaultCapacity,
        onChange,
        onBlur,
        error,
    };
}
